import React, { useState, useMemo, useRef, useEffect } from "react";
import { Download, Upload, AlertCircle, CheckCircle2, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { createTaxRule } from "../../store/slices/PayrollTaxTableSlice";
import { downloadTaxTableTemplate, readTaxTableWorkbook, validateTaxTableBatch } from "../../utils/taxTableImportUtils";

export default function ImportTaxTableModal({ open, onClose, existingRules, year, onSuccess }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [rawParsedData, setRawParsedData] = useState([]);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState('existing');

  // Determine active table
  const { activeTableNo, isActiveTableApproved } = useMemo(() => {
    if (!Array.isArray(existingRules)) return {
        activeTableNo: null,
        isActiveTableApproved: false,
    };
    const activeRow = existingRules.find((r) => String(r.HTT_STATUS).toUpperCase() === 'A');
    return { 
        activeTableNo: activeRow ? String(activeRow.HTT_TABLE_NO) : null,
        isActiveTableApproved: activeRow ? String(activeRow.HTT_IS_APPROVED) === 'True' : false,
    }
  }, [existingRules]);
  
  const hasActiveTable = Boolean(activeTableNo);

  // Dynamically compute 'rows' and validation whenever the mode or file data changes
  const rows = useMemo(() => {
    return validateTaxTableBatch(rawParsedData, existingRules, mode, activeTableNo);
  }, [rawParsedData, existingRules, mode, activeTableNo]);

  useEffect(() => {
    if (open) {
      setMode(hasActiveTable && !isActiveTableApproved ? 'existing' : 'new');
    }
  }, [open, hasActiveTable, isActiveTableApproved]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    try {
      const parsed = await readTaxTableWorkbook(file);
      setRawParsedData(parsed);
    } catch (err) {
      setError("Failed to read Excel file. Ensure you are using the correct template format.");
    }
    
    e.target.value = ""; // Reset input
  };

  const validRows = useMemo(() => rows.filter(r => r.errors.length === 0), [rows]);
  const invalidRows = useMemo(() => rows.filter(r => r.errors.length > 0), [rows]);

  const handleImport = async () => {
    if (!validRows.length) return;
    if (!year) {
      setError("Please select a Year in the main screen before importing.");
      return;
    }
    
    setImporting(true);
    let successCount = 0;
    let currentTableNo = mode === 'existing' ? activeTableNo : undefined;

    const rowsToImport = [...validRows];

    try {
      for (let i = 0; i < rowsToImport.length; i++) {
        const row = rowsToImport[i];
        
        const payload = {
          year: Number(year),
          minAmount: row.values.minAmount,
          maxAmount: row.values.maxAmount,
          taxPercentage: row.values.taxPercentage,
          deducAmount: row.values.deducAmount,
          incomeRange: row.values.incomeRange,
          tableNo: currentTableNo ? Number(currentTableNo) : undefined,
        };
        
        const result = await dispatch(createTaxRule(payload)).unwrap();
        successCount++;
        
        // If starting a new table, grab the newly generated tableNo from the first row's response 
        // to assign to the rest of the imported batch.
        if (mode === 'new' && i === 0) {
          const activeRow = result.rows.find(r => String(r.HTT_STATUS).toUpperCase() === 'A');
          currentTableNo = activeRow ? Number(activeRow.HTT_TABLE_NO) : undefined;
        }

        // Remove the successfully saved row from the preview
        setRawParsedData(prev => prev.filter(p => p._row !== row._row));
      }
      onSuccess(successCount);
      resetAndClose();
    } catch (err) {
      setError(err?.message || "An error occurred during import.");
    } finally {
      setImporting(false);
    }
  }; 

  const resetAndClose = () => {
    setRawParsedData([]);
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
        onClick={!importing ? resetAndClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import Tax Rules</h2>
            <p className="mt-0.5 text-sm text-gray-500">For year {year || '—'}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              type="button"
              onClick={downloadTaxTableTemplate}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            >
              <Download size={16} /> Template
            </button>
            <button 
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50"
            >
              <Upload size={16} /> Upload File
            </button>
            <input type="file" accept=".xlsx,.xls" className="hidden" ref={fileInputRef} onChange={handleFileChange} />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-100">
              <AlertCircle size={16} className="shrink-0" /> <span>{error}</span>
            </div>
          )}

          {/* Table Mode Selection */}
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Import Destination</h3>
            {hasActiveTable ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setMode('existing')}
                    disabled={importing || isActiveTableApproved}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      isActiveTableApproved
                        ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : mode === 'existing' 
                        ? 'border-primary-300 bg-primary-50 text-primary-700' 
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Add to current active table (No. {activeTableNo})
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('new')}
                    disabled={importing}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      mode === 'new' ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Start a new table
                  </button>
                </div>
                
                {isActiveTableApproved ? (
                  <p className="mt-2 text-xs text-red-600 font-medium">
                    The current active table (No. {activeTableNo}) is approved. You must start a new table to import rules.
                  </p>
                ) : mode === 'new' ? (
                  <p className="mt-2 text-xs text-amber-600">
                    This will deactivate the current table (No. {activeTableNo}) for {year} once imported.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-gray-500">
                No active table yet for {year} — this will create the first table.
              </p>
            )}
          </section>

          {/* Preview Grid */}
          {rows.length > 0 ? (
            <section className="flex flex-col h-full mt-2">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-700">Preview Data</span>
                <div className="flex items-center gap-4 text-[13px]">
                  <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                    <CheckCircle2 size={15} /> {validRows.length} Valid
                  </span>
                  <span className="flex items-center gap-1.5 text-red-600 font-medium">
                    <AlertCircle size={15} /> {invalidRows.length} Invalid
                  </span>
                </div>
              </div>

              <div className="border border-gray-200 rounded-md overflow-hidden flex-1 min-h-[250px]">
                <div className="overflow-y-auto max-h-[35vh]">
                  <table className="w-full text-left text-xs whitespace-nowrap">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-2.5 font-medium text-gray-600 w-16">Row</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Income Range</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Tax %</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Deduction Amount</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((r, i) => (
                        <tr key={i} className={r.errors.length ? "bg-red-50/50" : "hover:bg-gray-50/50"}>
                          <td className="px-4 py-2 text-gray-500">{r._row}</td>
                          <td className="px-4 py-2 text-gray-700">{r.values.incomeRange || '—'}</td>
                          <td className="px-4 py-2 font-medium">{r.values.taxPercentage ? `${r.values.taxPercentage}%` : '—'}</td>
                          <td className="px-4 py-2 text-gray-700">{r.values.deducAmount?.toLocaleString() || '—'}</td>
                          <td className="px-4 py-2">
                            {r.errors.length === 0 ? (
                              <span className="text-emerald-600 font-medium">Ready</span>
                            ) : (
                              <span className="text-red-600">{r.errors[0]}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-100 rounded-xl mt-4">
              <div className="h-12 w-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                <Upload className="text-gray-400" size={24} />
              </div>
              <p className="text-sm font-medium text-gray-900">No data to display</p>
              <p className="text-sm text-gray-500 mt-1">Please upload an Excel file to see the preview.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0 rounded-b-xl">
          <button 
            type="button"
            onClick={resetAndClose} 
            disabled={importing} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || validRows.length === 0 || !year}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
          >
            {importing ? "Importing..." : `Import ${validRows.length} rule(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}